import { makeAutoObservable, runInAction, reaction } from 'mobx';
import { EditorStore } from './EditorStore';
import { Renderer } from '../renderer/Renderer';
import { TimelineMode } from './commands/TimelineMode';
import { Framerate } from './models/Timeline';
import { TranscriptElement } from '../types.ts/transcript';
import { TimelineStore } from './lib/TimelineStore';
import { EditorChangeType } from './types';

/**
 * Store for managing EditorDemo UI state
 */
export class EditorDemoStore {
  // Player state
  isPlaying: boolean = false;
  currentTime: number = 0;
  /**
   * Index (word_index) of the word that should currently be highlighted based on
   * the playback time. When no word is active it will be set to null.
   */
  currentWordIndex: number | null = null;

  /**
   * Internal keeper for requestAnimationFrame orchestration so time updates are
   * coalesced into a single render frame. Updating MobX observables directly in
   * the renderer `onTimeChange` callback can introduce a lot of re‑rendering
   * overhead because that callback is often executed at >30 fps. Instead we
   * stash the latest time value received and flush it on the next rAF tick.
   */
  private _pendingTime: number | null = null;
  private _rafHandle: number | null = null;
  isSeeking: boolean = false;
  isDragging: boolean = false;

  // Timeline height
  timelineHeight: string = '40%';

  // Modal state
  isReplaceWordModalOpen: boolean = false;
  isPhotoSelectionModalOpen: boolean = false;
  selectedWordId: string | null = null;
  selectedWordText: string | null = null;
  originalWordText: string | null = null;
  selectedWordIds: string[] = [];

  // Cursor position for transcript operations
  cursorWordIndex: number | null = null;
  showCursor: boolean = true;
  cursorBlinkInterval: NodeJS.Timeout | null = null;

  // References
  renderer: Renderer | null = null;
  editorStore: EditorStore;

  // Timeline store
  timelineStore: TimelineStore | null = null;

  // Container reference for timeline positioning
  tracksContainerRef: React.RefObject<HTMLDivElement> | null = null;

  constructor(editorStore: EditorStore) {
    this.editorStore = editorStore;

    makeAutoObservable(this, {
      renderer: false,
      editorStore: false,
      tracksContainerRef: false,
      cursorBlinkInterval: false,
    });

    // Clean up resources when store is garbage collected
    const originalDispose = (this as any).__mobxDidRunLazyInitializers
      ? (this as any).__mobxDidRunLazyInitializers
      : null;

    (this as any).__mobxDidRunLazyInitializers = function () {
      if (originalDispose) {
        originalDispose.call(this);
      }
      this.stopCursorBlink();
    };

    // Setup reactions for MobX reactivity
    this.setupMobxReactions();
  }

  /**
   * Schedule a currentTime update coming from the renderer. The latest incoming
   * value is stored in a local variable and committed once per animation frame
   * to avoid triggering MobX reactions more often than the browser can paint.
   */
  scheduleTimeUpdate(time: number): void {
    // Store newest value; if multiple calls happen before the next frame we
    // only keep the latest.
    this._pendingTime = time;

    // If we have already scheduled a frame there's nothing else to do.
    if (this._rafHandle !== null) return;

    this._rafHandle = requestAnimationFrame(() => {
      // Allow new frames to be scheduled.
      this._rafHandle = null;

      if (this._pendingTime === null) return;

      const commitTime = this._pendingTime;
      this._pendingTime = null;

      runInAction(() => {
        this.currentTime = commitTime;
        this.currentWordIndex = this.computeCurrentWordIndex(commitTime);
      });
    });
  }

  /**
   * Compute the index of the transcript word that should be highlighted for
   * the given playback time.
   */
  private computeCurrentWordIndex(time: number): number | null {
    const activeTranscript: any[] = this.editorStore.activeTranscript;
    if (!activeTranscript || activeTranscript.length === 0) return null;

    // Linear scan is fine for demo usage. Optimize later if necessary.
    for (let i = 0; i < activeTranscript.length; i++) {
      const el = activeTranscript[i];
      if (el.type !== 'text') continue;

      const start: number | undefined = (el as any).timeline_ts ?? el.ts;
      const end: number | undefined =
        (el as any).timeline_end_ts ?? el.end_ts ?? start;

      if (start === undefined || end === undefined) continue;

      if (time >= start && time < end) {
        return el.word_index ?? null;
      }
    }

    return null;
  }

  /**
   * Sets the cursor position to the specified word index and starts cursor blinking
   */
  setCursorPosition(wordIndex: number): void {
    runInAction(() => {
      this.cursorWordIndex = wordIndex;
      this.showCursor = true;
      console.log(`Cursor position set at word index: ${wordIndex}`);

      // Start cursor blinking if not already blinking
      this.startCursorBlink();
    });
  }

  /**
   * Start cursor blinking animation
   */
  private startCursorBlink(): void {
    // Clear any existing interval first
    this.stopCursorBlink();

    // Create new blinking interval
    this.cursorBlinkInterval = setInterval(() => {
      runInAction(() => {
        this.showCursor = !this.showCursor;
      });
    }, 500); // 500ms blink rate
  }

  /**
   * Stop cursor blinking animation
   */
  private stopCursorBlink(): void {
    if (this.cursorBlinkInterval) {
      clearInterval(this.cursorBlinkInterval);
      this.cursorBlinkInterval = null;
    }
  }

  /**
   * Sets up MobX reactions to respond to store changes
   */
  private setupMobxReactions() {
    // React to timeline changes from EditorStore
    reaction(
      () => this.editorStore.timeline,
      (timeline) => {
        console.log('MobX reaction: Timeline changed');
        // Any additional logic that should happen when timeline changes
        // For example, recalculate default timeline scale
        if (document.querySelector('.timeline-container')) {
          const containerWidth =
            document.querySelector('.timeline-container')?.clientWidth || 800;
          this.calculateDefaultScale(containerWidth);
        }
      },
    );

    // React to current time changes
    reaction(
      () => this.currentTime,
      (time) => {
        // Update anything that depends on current time
        console.log('MobX reaction: Current time changed', time);
      },
    );
  }

  /**
   * Opens the replace word modal
   */
  openReplaceWordModal(wordId: string, wordText: string): void {
    // Get original word value from the original transcript if possible
    const originalElement =
      this.editorStore.editor.getTranscriptElementById(wordId);
    let originalText = wordText; // Default to current value

    // If we can get the original element, use its unmodified value
    if (originalElement && originalElement.type === 'text') {
      // If the element has an old_value, it was previously modified
      if (originalElement.old_value) {
        originalText = originalElement.old_value;
      } else {
        // Otherwise use the value from the original transcript
        originalText = originalElement.value || wordText;
      }
    }

    runInAction(() => {
      this.selectedWordId = wordId;
      this.selectedWordText = wordText;
      this.originalWordText = originalText;
      this.isReplaceWordModalOpen = true;
    });
  }

  /**
   * Closes the replace word modal
   */
  closeReplaceWordModal(): void {
    runInAction(() => {
      this.isReplaceWordModalOpen = false;
      this.selectedWordId = null;
      this.selectedWordText = null;
      this.originalWordText = null;
    });
  }

  /**
   * Replaces word(s) in the transcript
   */
  replaceWord(newText: string, replaceAll: boolean = false): void {
    if (!this.selectedWordId) return;

    console.log(
      `Replacing word ID ${this.selectedWordId} with "${newText}", replaceAll: ${replaceAll}`,
    );
    // TODO: Replace with actual command when we integrate transcript fully

    // this.editorStore.editor.commandTranscriptReplaceWord(
    //   this.selectedWordId,
    //   newText,
    //   { replaceAll },
    // );
  }

  /**
   * Opens the photo selection modal
   */
  openPhotoSelectionModal(wordIds: string[]): void {
    runInAction(() => {
      this.selectedWordIds = [...wordIds];
      this.isPhotoSelectionModalOpen = true;
    });
  }

  /**
   * Closes the photo selection modal
   */
  closePhotoSelectionModal(): void {
    runInAction(() => {
      this.isPhotoSelectionModalOpen = false;
      this.selectedWordIds = [];
    });
  }

  /**
   * Attaches a photo to the selected transcript words
   */
  attachPhotoToTranscript(photoUrl: string): void {
    if (this.selectedWordIds.length === 0) return;

    console.log(
      `Attaching photo "${photoUrl}" to words: ${this.selectedWordIds.join(
        ', ',
      )}`,
    );
    // TODO: Attach photo needs to be redone.
    // this.editorStore.editor.commandAttachPhoto(this.selectedWordIds, photoUrl);
  }

  /**
   * This function has been replaced with a direct call to editor.commandCutTranscriptSegment in EditorDemo.tsx
   * @deprecated
   */
  cutTranscriptWords(wordIds: string[]): void {
    console.warn(
      'This method is deprecated and should not be used. Use editor.commandCutTranscriptSegment directly.',
    );
  }

  /**
   * Pastes previously cut transcript words at the specified position
   * @param time The time position to paste at
   * @param startInitialIndex The start initial index of the words to paste
   * @param endInitialIndex The end initial index of the words to paste
   */
  pasteTranscriptWords(
    time: number,
    startInitialIndex: number,
    endInitialIndex: number,
  ): void {
    if (this.editorStore.editor.getCutTranscriptInitialIndexes().length === 0) {
      console.warn('No cut words available to paste');
      return;
    }

    console.log(
      `Pasting cut words at time ${time}s with indices ${startInitialIndex}-${endInitialIndex}`,
    );

    // Create a PasteTranscriptSegment object and pass it as an array
    const pasteSegment = {
      intoTime: time,
      startInitialIndex: startInitialIndex,
      endInitialIndex: endInitialIndex,
    };

    this.editorStore.editor.commandPasteTranscriptWords([pasteSegment]);
  }

  /**
   * Checks if there are cut transcript words available for pasting
   */
  hasCutTranscriptWords(): boolean {
    const cutIndexes = this.editorStore.editor.getCutTranscriptInitialIndexes();
    return cutIndexes && cutIndexes.length > 0;
  }

  /**
   * Set the renderer reference
   */
  setRenderer(renderer: Renderer) {
    this.renderer = renderer;
  }

  /**
   * Set the tracks container reference and initialize the timeline store
   */
  setTracksContainerRef(containerRef: React.RefObject<HTMLDivElement>) {
    this.tracksContainerRef = containerRef;
    this.initializeTimelineStore();
  }

  /**
   * Initialize the timeline store
   */
  private initializeTimelineStore() {
    if (!this.tracksContainerRef) return;

    this.timelineStore = new TimelineStore(
      this.editorStore.timeline.duration || 0,
      this.tracksContainerRef,
      this.setTime.bind(this),
      (mode: TimelineMode) => this.editorStore.editor.setTimelineMode(mode),
      () => this.editorStore.editor.getFramerate(),
      (fps: Framerate) => this.editorStore.editor.setFramerate(fps),
      () => this.editorStore.editor.getSnapToFrame(),
      (snap: boolean) => this.editorStore.editor.setSnapToFrame(snap),
    );
  }

  // Throttle the time updates to avoid excessive renderer loading
  private lastTimeUpdate: number = 0;
  private pendingTimeUpdate: boolean = false;
  private timeUpdateTimeoutId: NodeJS.Timeout | null = null;
  private timeUpdateThrottleInterval: number = 100; // ms

  /**
   * Set the current playback time
   */
  setTime(time: number, updateRenderer: boolean = true) {
    // Get the snap-to-frame option from the editor
    const shouldSnapToFrame = this.editorStore.editor.getSnapToFrame();

    // Snap time to frame if enabled
    let processedTime = time;
    if (shouldSnapToFrame) {
      processedTime = this.editorStore.editor.snapTimeToFrame(time);
    }

    // Clamp time to valid range
    const newTime = Math.max(
      0,
      Math.min(processedTime, this.editorStore.timeline.duration || 0),
    );

    // Skip if time hasn't changed significantly (within 0.01 seconds)
    if (Math.abs(this.currentTime - newTime) < 0.01) {
      return;
    }

    // Always update the local time state immediately
    runInAction(() => {
      this.currentTime = newTime;
    });

    // Skip renderer update if not requested or no renderer available
    if (!updateRenderer || !this.renderer) {
      return;
    }

    const now = Date.now();

    // If we're already updating or have updated very recently, throttle updates
    if (
      this.pendingTimeUpdate ||
      now - this.lastTimeUpdate < this.timeUpdateThrottleInterval
    ) {
      if (!this.pendingTimeUpdate) {
        this.pendingTimeUpdate = true;

        // Clear any existing timeout to prevent duplicate updates
        if (this.timeUpdateTimeoutId) {
          clearTimeout(this.timeUpdateTimeoutId);
        }

        // Schedule an update after the throttle interval
        this.timeUpdateTimeoutId = setTimeout(() => {
          this.pendingTimeUpdate = false;

          // Check if time is still the same as when we scheduled the update
          if (Math.abs(this.currentTime - newTime) < 0.01) {
            this.updateRendererTime(this.currentTime);
          } else {
            // Time has changed again, use the current time
            this.updateRendererTime(this.currentTime);
          }
        }, this.timeUpdateThrottleInterval);
      }
      return;
    }

    // Otherwise update immediately
    this.updateRendererTime(newTime);
  }

  /**
   * Updates the renderer time directly
   */
  private updateRendererTime(time: number) {
    if (!this.renderer) return;

    this.lastTimeUpdate = Date.now();

    this.renderer.setTime(time).catch((error) => {
      console.error('Error updating time:', error);
    });
  }

  /**
   * Start playback
   */
  play() {
    if (this.renderer) {
      console.log('Attempting to play video');
      this.renderer
        .play()
        .then((m: any) => {
          console.log(m);
          console.log('Play command successful');
          // The onPlay callback should handle isPlaying=true
        })
        .catch((error) => {
          console.error('Error playing video:', error);
        });
    }
  }

  /**
   * Pause playback
   */
  pause() {
    if (this.renderer) {
      console.log('Attempting to pause video');
      this.renderer
        .pause()
        .then(() => {
          console.log('Pause command successful');
          // The onPause callback should handle isPlaying=false
        })
        .catch((error) => {
          console.error('Error pausing video:', error);
        });
    }
  }

  /**
   * Toggle playback state
   */
  togglePlayback() {
    console.log('Toggle playback, current state:', this.isPlaying);
    if (this.isPlaying) {
      this.pause();
    } else {
      this.play();
    }
  }

  /**
   * Set the playhead dragging state
   */
  setPlayheadDragging(isDragging: boolean) {
    if (this.timelineStore) {
      this.timelineStore.setPlayheadDragging(isDragging);
    }
  }

  /**
   * Get playhead dragging state
   */
  get isPlayheadDragging(): boolean {
    return this.timelineStore?.isPlayheadDragging || false;
  }

  /**
   * Set the timeline scale (zoom level)
   */
  setTimelineScale(scale: number) {
    if (this.timelineStore) {
      this.timelineStore.setTimelineScale(scale);
    }
  }

  /**
   * Get the current timeline scale
   */
  get timelineScale(): number {
    return this.timelineStore?.timelineScale || 100;
  }

  /**
   * Get the default timeline scale
   */
  get defaultTimelineScale(): number {
    return this.timelineStore?.defaultTimelineScale || 100;
  }

  /**
   * Zoom in the timeline
   */
  zoomIn() {
    if (this.timelineStore) {
      this.timelineStore.zoomIn();
    }
  }

  /**
   * Zoom out the timeline
   */
  zoomOut() {
    if (this.timelineStore) {
      this.timelineStore.zoomOut();
    }
  }

  /**
   * Calculate default timeline scale based on timeline duration and container width
   */
  calculateDefaultScale(containerWidth: number) {
    if (this.timelineStore) {
      this.timelineStore.calculateDefaultScale(containerWidth);
    } else if (this.tracksContainerRef) {
      // Initialize timeline store if it doesn't exist yet
      this.initializeTimelineStore();
    }
  }

  /**
   * Handle timeline click to position playhead
   */
  handleTimelineClick(e: React.MouseEvent<HTMLDivElement>) {
    if (this.timelineStore) {
      this.timelineStore.handleTimelineClick(e);
    }
  }

  /**
   * Set track height for a specific track
   */
  setTrackHeight(trackId: string, height: number) {
    if (this.timelineStore) {
      this.timelineStore.setTrackHeight(trackId, height);
    }
  }

  /**
   * Get the height for a track (with defaults based on type)
   */
  getTrackHeight(trackId: string, type: string): number {
    return this.timelineStore?.getTrackHeight(trackId, type) || 60;
  }

  /**
   * Calculate the total height of all tracks
   */
  get totalTracksHeight(): number {
    return this.editorStore.timeline.tracks.reduce((total, track) => {
      return total + this.getTrackHeight(track.id, track.type);
    }, 0);
  }

  /**
   * Get the current timeline mode
   */
  get timelineMode(): TimelineMode {
    return this.timelineStore?.timelineMode || TimelineMode.DEFAULT;
  }

  /**
   * Set the timeline mode
   */
  setTimelineMode(mode: TimelineMode): void {
    if (this.timelineStore) {
      this.timelineStore.setTimelineMode(mode);
    }
  }

  /**
   * Undo the last operation
   */
  undo(): boolean {
    return this.editorStore.editor.undo();
  }

  /**
   * Redo a previously undone operation
   */
  redo(): boolean {
    return this.editorStore.editor.redo();
  }

  /**
   * Gets the current framerate
   */
  getFramerate(): Framerate {
    return this.timelineStore?.getFramerate() || Framerate.FPS_30;
  }

  /**
   * Sets the framerate
   */
  setFramerate(fps: Framerate): void {
    if (this.timelineStore) {
      this.timelineStore.setFramerate(fps);
    }
  }

  /**
   * Gets the current snap-to-frame setting
   */
  getSnapToFrame(): boolean {
    return this.timelineStore?.getSnapToFrame() || false;
  }

  /**
   * Sets the snap-to-frame setting
   */
  setSnapToFrame(snapToFrame: boolean): void {
    if (this.timelineStore) {
      this.timelineStore.setSnapToFrame(snapToFrame);
    }
  }

  /**
   * Gets the timeline position manager
   */
  get positionManager() {
    return this.timelineStore?.positionManager;
  }

  /**
   * Converts the current time to a frame number
   */
  getCurrentFrame(): number {
    return this.editorStore.editor.timeToFrame(this.currentTime);
  }

  /**
   * Sets the current time to a specific frame
   */
  setCurrentFrame(frame: number): void {
    const time = this.editorStore.editor.frameToTime(frame);
    this.setTime(time);
  }

  /**
   * Loads a sample transcript for demo purposes
   */
  loadSampleTranscript(trackId: string): void {
    const sampleTranscript: TranscriptElement[] = [
      {
        type: 'text',
        value: 'Hello',
        old_value: null,
        ts: 0.0,
        end_ts: 0.5,
        initial_index: 0,
      },
      {
        type: 'punct',
        value: ' ',
        old_value: null,
        ts: null,
        end_ts: null,
        initial_index: 1,
      },
      {
        type: 'text',
        value: 'and',
        old_value: null,
        ts: 0.6,
        end_ts: 0.9,
        initial_index: 2,
      },
      {
        type: 'punct',
        value: ' ',
        old_value: null,
        ts: null,
        end_ts: null,
        initial_index: 3,
      },
      {
        type: 'text',
        value: 'welcome',
        old_value: null,
        ts: 1.0,
        end_ts: 1.5,
        initial_index: 4,
      },
      {
        type: 'punct',
        value: ' ',
        old_value: null,
        ts: null,
        end_ts: null,
        initial_index: 5,
      },
      {
        type: 'text',
        value: 'to',
        old_value: null,
        ts: 1.6,
        end_ts: 1.8,
        initial_index: 6,
      },
      {
        type: 'punct',
        value: ' ',
        old_value: null,
        ts: null,
        end_ts: null,
        initial_index: 7,
      },
      {
        type: 'text',
        value: 'our',
        old_value: null,
        ts: 1.9,
        end_ts: 2.1,
        initial_index: 8,
      },
      {
        type: 'punct',
        value: ' ',
        old_value: null,
        ts: null,
        end_ts: null,
        initial_index: 9,
      },
      {
        type: 'text',
        value: 'video',
        old_value: null,
        ts: 2.2,
        end_ts: 2.6,
        initial_index: 10,
      },
      {
        type: 'punct',
        value: ' ',
        old_value: null,
        ts: null,
        end_ts: null,
        initial_index: 11,
      },
      {
        type: 'text',
        value: 'editor',
        old_value: null,
        ts: 2.7,
        end_ts: 3.2,
        initial_index: 12,
      },
      {
        type: 'punct',
        value: ' ',
        old_value: null,
        ts: null,
        end_ts: null,
        initial_index: 13,
      },
      {
        type: 'text',
        value: 'demo',
        old_value: null,
        ts: 3.3,
        end_ts: 3.7,
        initial_index: 14,
      },
      {
        type: 'punct',
        value: '.',
        old_value: null,
        ts: null,
        end_ts: null,
        initial_index: 15,
      },
      {
        type: 'punct',
        value: ' ',
        old_value: null,
        ts: null,
        end_ts: null,
        initial_index: 16,
      },
      {
        type: 'text',
        value: 'This',
        old_value: null,
        ts: 4.0,
        end_ts: 4.3,
        initial_index: 17,
      },
      {
        type: 'punct',
        value: ' ',
        old_value: null,
        ts: null,
        end_ts: null,
        initial_index: 18,
      },
      {
        type: 'text',
        value: 'shows',
        old_value: null,
        ts: 4.4,
        end_ts: 4.8,
        initial_index: 19,
      },
      {
        type: 'punct',
        value: ' ',
        old_value: null,
        ts: null,
        end_ts: null,
        initial_index: 20,
      },
      {
        type: 'text',
        value: 'how',
        old_value: null,
        ts: 4.9,
        end_ts: 5.1,
        initial_index: 21,
      },
      {
        type: 'punct',
        value: ' ',
        old_value: null,
        ts: null,
        end_ts: null,
        initial_index: 22,
      },
      {
        type: 'text',
        value: 'transcripts',
        old_value: null,
        ts: 5.2,
        end_ts: 5.9,
        initial_index: 23,
      },
      {
        type: 'punct',
        value: ' ',
        old_value: null,
        ts: null,
        end_ts: null,
        initial_index: 24,
      },
      {
        type: 'text',
        value: 'integrate',
        old_value: null,
        ts: 6.0,
        end_ts: 6.5,
        initial_index: 25,
      },
      {
        type: 'punct',
        value: ' ',
        old_value: null,
        ts: null,
        end_ts: null,
        initial_index: 26,
      },
      {
        type: 'text',
        value: 'with',
        old_value: null,
        ts: 6.6,
        end_ts: 6.9,
        initial_index: 27,
      },
      {
        type: 'punct',
        value: ' ',
        old_value: null,
        ts: null,
        end_ts: null,
        initial_index: 28,
      },
      {
        type: 'text',
        value: 'video',
        old_value: null,
        ts: 7.0,
        end_ts: 7.4,
        initial_index: 29,
      },
      {
        type: 'punct',
        value: ' ',
        old_value: null,
        ts: null,
        end_ts: null,
        initial_index: 30,
      },
      {
        type: 'text',
        value: 'editing',
        old_value: null,
        ts: 7.5,
        end_ts: 8.0,
        initial_index: 31,
      },
      {
        type: 'punct',
        value: '.',
        old_value: null,
        ts: null,
        end_ts: null,
        initial_index: 32,
      },
    ];

    // Load the transcript into the editor
    this.editorStore.editor.loadTranscript(sampleTranscript, trackId);

    console.log(
      'Sample transcript loaded with',
      sampleTranscript.length,
      'elements',
    );
  }

  // Methods for direct transcript modifications removed

  /**
   * Jumps to the time position of a transcript element
   * @param elementId ID of the transcript element to jump to
   * @param timelineTs Optional timeline timestamp (absolute position) to use instead of element.ts
   */
  jumpToTranscriptElement(elementId: string, timelineTs?: number): void {
    console.log(
      `Jumping to transcript element ${elementId}, provided timelineTs: ${timelineTs}`,
    );

    // If a valid timeline timestamp is provided, use it directly
    if (timelineTs !== undefined && !isNaN(timelineTs)) {
      console.log(`Using provided timeline timestamp: ${timelineTs}`);
      this.setTime(timelineTs);
      return;
    }

    // Otherwise, try to get the element and its timestamp
    const element = this.editorStore.editor.getTranscriptElementById(elementId);

    if (element && element.type === 'text') {
      // Look for any timeline timestamp that might be attached to the element
      const elementTimelineTs = (element as any).timeline_ts;

      if (elementTimelineTs !== undefined && !isNaN(elementTimelineTs)) {
        console.log(`Using element's timeline_ts: ${elementTimelineTs}`);
        this.setTime(elementTimelineTs);
      }
      // Fall back to the element's original timestamp
      else if (element.ts !== undefined && !isNaN(element.ts)) {
        console.log(`Falling back to element's original ts: ${element.ts}`);
        this.setTime(element.ts);
      } else {
        console.warn(`No valid timestamp found for element ${elementId}`);
      }
    } else {
      console.warn(`Element ${elementId} not found or not a text element`);
    }
  }
}
